import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Input,
  Select,
  Button,
  Tooltip,
  message,
  Empty,
  Switch,
  Popconfirm,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { PlusCircleOutlined } from '@ant-design/icons';

import Table from '@components/Table';
import AdminActions from '@components/AdminActions';
import useSearchParams from '@hooks/useSearchParams';
import useSwr from '@hooks/useSwr';
import SessionContext from '@providers/Session/SessionContext';
import { format } from 'date-fns/esm';
import { enUS, ptBR } from 'date-fns/locale';
import rolesConstants from '@utils/rolesConstants';
import { ColumnsType } from 'antd/lib/table';
import { CreateAlarmSteps } from '@components/Insights/CreateAlarm/CreateAlarmSteps';
import { useAlarmMenuFilter } from '@hooks/useAlarmMenuFilter';
import * as Styled from './Styles';

const { Search } = Input;

export interface IAlert {
  dataCriacao: string;
  dataUltimaAlteracao: string;
  customerId: number;
  level: string | null;
  nomeAlarme: string;
  valorParaDisparo: number;
  status: string;
  quantidade: number | null;
  qtdMin: number | null;
  qtdMax: number | null;
  idAlarme: string;
  nomeGrafico: string;
  dataHistoricoAlarme: string | null;
  chatId?: string[] | null;
  email?: string[] | null;
  contato?: string;
  parametro?: string;
  tipoAlarme?: string;
}

interface PageDashBoardResult<T> {
  content: T[];
  totalPages: number;
  currentPage: number;
  pageSize: number;
  totalElements: number;
}

const AlertList: React.FC = () => {
  const { t, i18n } = useTranslation([
    'alerts',
    'attributes',
    'create-alarm-step',
  ]);
  const currentLanguage = i18n.language;

  const [nomeAlarmeValue, setNomeAlarmeValue] = useState('');
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [alertToDelete, setAlertToDelete] = useState<string | null>(null);
  const [alertToUpdate, setAlertToUpdate] = useState<string | null>(null);
  const [tipoAlarmeValue, setTipoAlarmeValue] = useState<string | undefined>();
  const [updateStatusParams, setUpdateStatusParams] = useState<{
    idTask: string;
    status: string;
  } | null>(null);

  const {
    customer: { customerLogged },
    effectiveRoles,
  } = useContext(SessionContext);

  const hasManagePermission = effectiveRoles?.portal?.includes(
    rolesConstants.INSIGHTS_ALARM_MANAGE_CONFIGURATION,
  );

  const {
    allMenuFilter,
    defaultFilterValue,
    hasSimcardsPermission,
    hasTransactionsPermission,
  } = useAlarmMenuFilter({
    simcardsPermission: rolesConstants.INSIGHTS_ALARM_CONFIGURATION,
    transactionsPermission: rolesConstants.INSIGHTS_TRANSACTIONS_VIEW,
  });

  const hasAnyAlarmPermission =
    hasSimcardsPermission || hasTransactionsPermission;

  const customerId = customerLogged?.id;

  const {
    setSearchParams,
    hasValuesInExtraParams,
    clearSearchParams,
    searchParams,
  } = useSearchParams({
    type: 'pagination',
    extraParams: [
      'nomeAlarme',
      'customerIdString',
      'status',
      'tipoAlarme',
      'nomeGrafico',
    ],
  });

  const {
    nomeAlarme,
    status,
    tipoAlarme,
    nomeGrafico,
    page = 1,
    linesPerPage = 20,
  } = useMemo(() => searchParams, [searchParams]);

  const [activeMenuFilter, setActiveMenuFilter] = useState<string>(
    nomeGrafico || '',
  );

  useEffect(() => {
    setActiveMenuFilter(nomeGrafico || '');
  }, [nomeGrafico]);

  useEffect(() => {
    setNomeAlarmeValue(nomeAlarme || '');
  }, [nomeAlarme]);

  useEffect(() => {
    setTipoAlarmeValue(tipoAlarme);
  }, [tipoAlarme]);

  const {
    data: alertsResponse,
    isLoading: isLoadingAlerts,
    mutate: mutateAlertsList,
  } = useSwr<PageDashBoardResult<IAlert>>(
    customerId
      ? '/service-proxy/dashboard/simcard_online/alarme/kapacitor/alerta_task/listar'
      : null,
    {
      params: {
        ...(activeMenuFilter ? { nomeGrafico: activeMenuFilter } : {}),
        ...(nomeAlarme ? { nomeAlarme } : {}),
        ...(status ? { status } : {}),
        ...(tipoAlarme ? { tipoAlarme } : {}),
        page: Number(page),
        size: Number(linesPerPage),
      },
      headers: {
        customerId,
      },
    },
  );

  const { isLoading: isDeletingAlert } = useSwr<{ success: boolean }>(
    alertToDelete
      ? `/service-proxy/dashboard/simcard_online/alarme/kapacitor/alerta_task/excluir/${alertToDelete}`
      : null,
    {
      headers: {
        customerId,
      },
      method: 'DELETE',
    },
    {
      onSuccess: () => {
        message.success(t('alerts:msgs.alert-deleted'));
        setAlertToDelete(null);
        mutateAlertsList();
      },
      onError: () => {
        message.error(t('alerts:msgs.alert-deletion-error'));
        setAlertToDelete(null);
      },
    },
  );

  const { isLoading: isUpdatingStatus } = useSwr<{ success: boolean }>(
    updateStatusParams
      ? `/service-proxy/dashboard/simcard_online/alarme/kapacitor/alerta_task/status/${updateStatusParams.idTask}/${updateStatusParams.status}`
      : null,
    {
      method: 'PATCH',
      headers: {
        customerId,
      },
    },
    {
      onSuccess: () => {
        message.success(t('alerts:msgs.alert-updated'));
        setUpdateStatusParams(null);
        setAlertToUpdate(null);
        mutateAlertsList();
      },
      onError: () => {
        message.error(t('alerts:msgs.alert-update-error'));
        setUpdateStatusParams(null);
        setAlertToUpdate(null);
      },
    },
  );

  const alerts = useMemo(() => {
    return alertsResponse?.content || [];
  }, [alertsResponse]);

  const handleStatusChange = useCallback(
    (record: IAlert, newStatus: string) => {
      setAlertToUpdate(record.idAlarme);
      setUpdateStatusParams({
        idTask: record.idAlarme,
        status: newStatus,
      });
    },
    [],
  );

  const handleActions = useCallback(
    async (actionType: string, record: IAlert) => {
      if (actionType === 'DELETE') {
        setAlertToDelete(record.idAlarme);
      }
    },
    [],
  );

  const handleNewAlert = useCallback(() => {
    setIsModalVisible(true);
  }, []);

  const alarmTypeOptions = useMemo(
    () => [
      {
        label: t('alerts:table-filter.by-alarm-type-threshold'),
        value: 'threshold',
      },
      {
        label: t(
          'alerts:table-filter.by-alarm-type-variation-percentual-history',
        ),
        value: 'variacao_percentual',
      },
      {
        label: t('alerts:table-filter.by-alarm-type-seasonal'),
        value: 'sazonal',
      },
    ],
    [t],
  );

  useEffect(() => {
    if (
      activeMenuFilter === 'simcard_produto' &&
      !hasSimcardsPermission &&
      hasTransactionsPermission
    ) {
      setActiveMenuFilter(defaultFilterValue);
      setSearchParams({ page: 1 });
    }
  }, [
    activeMenuFilter,
    hasSimcardsPermission,
    hasTransactionsPermission,
    defaultFilterValue,
    setSearchParams,
  ]);

  const dateFormatConfig = useMemo(() => {
    if (currentLanguage === 'en') {
      return {
        locale: enUS,
        dateTimeFormat: 'MM/dd/yyyy HH:mm',
      };
    }
    return {
      locale: ptBR,
      dateTimeFormat: 'dd/MM/yyyy HH:mm',
    };
  }, [currentLanguage]);

  const formatDateTime = useCallback(
    (dateString: string) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return format(date, dateFormatConfig.dateTimeFormat, {
        locale: dateFormatConfig.locale,
      });
    },
    [dateFormatConfig],
  );

  const columns: ColumnsType<IAlert> = useMemo(() => {
    const baseColumns: ColumnsType<IAlert> = [
      {
        title: t('alerts:table-attributes.alert-name-field'),
        dataIndex: 'nomeAlarme',
        align: 'center' as const,
      },
      {
        title: t('alerts:table-attributes.alarm-type-field'),
        dataIndex: 'tipoAlarme',
        align: 'center' as const,
        render: (alarmType: string) => {
          if (!alarmType) return '';
          return t(`alerts:alarm-types.${alarmType}`);
        },
      },
      {
        title: t('alerts:table-attributes.graph-name-field'),
        dataIndex: 'nomeGrafico',
        align: 'center' as const,
        render: (graphName: string) => {
          if (!graphName) return '';
          return t(`alerts:graph-names.${graphName}`);
        },
      },
      {
        title: t('alerts:table-attributes.trigger-metric-field'),
        dataIndex: 'triggerMetric',
        align: 'center' as const,
        render: (_: undefined, record: IAlert) => {
          if (record.qtdMin !== null && record.qtdMin !== undefined) {
            return t('alerts:table-attributes.trigger-metric-qtcMin-field');
          }
          if (record.qtdMax !== null && record.qtdMax !== undefined) {
            return t('alerts:table-attributes.trigger-metric-qtcMax-field');
          }
          return '';
        },
      },
      {
        title: t('alerts:table-attributes.value-to-trigger-field'),
        dataIndex: 'valorParaDisparo',
        align: 'center' as const,
        render: (value: number) => {
          if (!value && value !== 0) return '';
          return value.toString();
        },
      },
      {
        title: t('alerts:table-attributes.contact-field'),
        dataIndex: 'contato',
        align: 'center' as const,
      },
      {
        title: t('alerts:table-attributes.parameter-field'),
        dataIndex: 'parametro',
        align: 'center' as const,
      },
      {
        title: t('alerts:table-attributes.created-at-field'),
        dataIndex: 'dataCriacao',
        align: 'center' as const,
        render: (date: string) => formatDateTime(date),
      },
    ];

    if (hasManagePermission) {
      baseColumns.push({
        title: t('attributes:actions'),
        dataIndex: 'actions',
        width: 200,
        align: 'center' as const,
        render: (text: string, record: IAlert) => {
          const isActive = record.status === 'ativo';
          const isOwner = customerId === record.customerId;
          const isLoading =
            isUpdatingStatus && alertToUpdate === record.idAlarme;

          return (
            <Styled.SwitchContainer>
              <Popconfirm
                title={
                  isActive
                    ? t('alerts:msgs.confirm-deactivate')
                    : t('alerts:msgs.confirm-activate')
                }
                onConfirm={() => {
                  handleStatusChange(record, isActive ? 'inativo' : 'ativo');
                }}
                okText={t('alerts:buttons.confirm')}
                cancelText={t('alerts:buttons.cancel')}
                disabled={!isOwner || !hasManagePermission}
                overlayClassName="alert-status-popconfirm"
                getPopupContainer={trigger => {
                  return trigger.parentElement || document.body;
                }}
              >
                <Switch
                  checked={isActive}
                  disabled={!isOwner || !hasManagePermission}
                  loading={isLoading}
                  checkedChildren={t('alerts:status.active')}
                  unCheckedChildren={t('alerts:status.inactive')}
                />
              </Popconfirm>
              <AdminActions
                alertDelete={t('alerts:titles.delete-modal-content')}
                entityToExclude={record.nomeAlarme}
                onDelete={() => handleActions('DELETE', record)}
                isButtonLink={false}
                isDeleteDisabled={
                  (isDeletingAlert && alertToDelete === record.idAlarme) ||
                  customerId !== record.customerId
                }
                hasDeletePermission={
                  rolesConstants.INSIGHTS_ALARM_MANAGE_CONFIGURATION
                }
                needPermission
              />
            </Styled.SwitchContainer>
          );
        },
      });
    }

    return baseColumns;
  }, [
    t,
    hasManagePermission,
    formatDateTime,
    isDeletingAlert,
    alertToDelete,
    customerId,
    isUpdatingStatus,
    alertToUpdate,
    handleActions,
    handleStatusChange,
  ]);

  const tableFilters = useMemo(
    () => [
      {
        visible: true,
        col: {
          lg: 12,
          xl: 8,
          xs: 24,
        },
        label: t('alerts:table-label-filter.graph-name-label'),
        element: (
          <Select
            optionFilterProp="label"
            options={allMenuFilter}
            style={{ width: '100%' }}
            showSearch
            value={activeMenuFilter}
            onChange={value => {
              setActiveMenuFilter(value);
              setSearchParams({ nomeGrafico: value, page: 1 });
            }}
            allowClear
          />
        ),
      },
      {
        visible: true,
        col: {
          lg: 12,
          xl: 8,
          xs: 24,
        },
        label: t('alerts:table-label-filter.alert-name-label'),
        element: (
          <Search
            onSearch={value => {
              setSearchParams({ nomeAlarme: value, page: 1 });
            }}
            onChange={({ target: { value } }) => setNomeAlarmeValue(value)}
            value={nomeAlarmeValue}
            allowClear
          />
        ),
      },
      {
        visible: true,
        col: {
          lg: 12,
          xl: 8,
          xs: 24,
        },
        label: t('alerts:table-label-filter.alarm-type-label'),
        element: (
          <Select
            optionFilterProp="label"
            options={alarmTypeOptions}
            style={{ width: '100%' }}
            allowClear
            showSearch
            value={tipoAlarmeValue}
            onChange={value => {
              setTipoAlarmeValue(value);
              setSearchParams({
                tipoAlarme: value,
                page: 1,
              });
            }}
          />
        ),
      },
      {
        visible: true,
        col: {
          lg: 12,
          xl: 8,
          xs: 24,
        },
        label: t('alerts:table-label-filter.status-label'),
        element: (
          <Select
            optionFilterProp="label"
            options={[
              {
                label: t('alerts:table-filter.by-status-active'),
                value: 'ativo',
              },
              {
                label: t('alerts:table-filter.by-status-inactive'),
                value: 'inativo',
              },
            ]}
            style={{ width: '100%' }}
            allowClear
            showSearch
            value={status}
            onChange={value => {
              setSearchParams({
                status: value,
                page: 1,
              });
            }}
          />
        ),
      },
    ],
    [
      t,
      allMenuFilter,
      activeMenuFilter,
      nomeAlarmeValue,
      alarmTypeOptions,
      tipoAlarmeValue,
      status,
      setSearchParams,
    ],
  );

  const extraActions = useMemo(() => {
    if (!hasAnyAlarmPermission) return null;

    return (
      <Tooltip placement="top" title={t('alerts:titles.create-alert')}>
        <Button icon={<PlusCircleOutlined />} onClick={handleNewAlert} />
      </Tooltip>
    );
  }, [hasAnyAlarmPermission, t, handleNewAlert]);

  const onCloseModal = useCallback(() => {
    setIsModalVisible(false);
  }, []);

  return (
    <Styled.Container>
      {isModalVisible && (
        <CreateAlarmSteps
          visible={isModalVisible}
          onClose={onCloseModal}
          onAlarmCreated={mutateAlertsList}
        />
      )}

      <Table
        columns={columns}
        data={alerts.map((item, index) => ({
          ...item,
          key: `alert-row-${index}`,
          id: item.idAlarme || `alert-${index}`,
        }))}
        loading={isLoadingAlerts}
        title={t('alerts:titles.alerts')}
        tableKey="alerts"
        customRowKey="key"
        filters={{
          list: tableFilters,
          hasValuesInFilters: hasValuesInExtraParams,
          onClearFilters: () => clearSearchParams(),
          openByDefault: true,
        }}
        pagination={{
          page: Number(page),
          linesPerPage: Number(linesPerPage),
          total: alertsResponse?.totalElements || 0,
        }}
        onTableChange={setSearchParams}
        extraActions={extraActions}
        disabledPaginationWhenAll={false}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t('alerts:table.notFoundMessage')}
            />
          ),
        }}
      />
    </Styled.Container>
  );
};

export default AlertList;
